import React from 'react';
import { twMerge } from 'tailwind-merge';
import { AvatarUI } from '@components/main-components/avatar/Avatar';
import {
  type FragmentType,
  getFragment,
  gql,
  GroupedUsersInfoFragmentDoc,
} from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';

const NOTIFICATION_AVATAR_SIZE = {
  MOBILE: 12,
  TABLET: 16,
};

const SOCIAL_PROOF_AVATAR_SIZE = {
  MOBILE: 16,
  TABLET: 24,
};

const MAX_AVATARS_TO_SHOW = 3;

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment GroupedUsersInfo on User {
    id
    username
    publicAddress
    webappUri
    avatar {
      id
      url
    }
  }
`);

function getAvatarSize(variant: 'notification' | 'social-proof') {
  switch (variant) {
    case 'notification':
      return NOTIFICATION_AVATAR_SIZE;
    case 'social-proof':
      return SOCIAL_PROOF_AVATAR_SIZE;
    default:
      return NOTIFICATION_AVATAR_SIZE;
  }
}

export const GroupedUsersAvatar = ({
  users,
  variant,
}: {
  users: FragmentType<GroupedUsersInfoFragmentDoc>[];
  variant: 'notification' | 'social-proof';
}) => {
  const usersData = React.useMemo(
    () => users.map(user => getFragment(GroupedUsersInfoFragmentDoc, user)),
    [users],
  );

  const avatarSize = getAvatarSize(variant);

  const usersDataLength = usersData.length;

  const maxToShow = usersDataLength <= MAX_AVATARS_TO_SHOW ? usersDataLength : MAX_AVATARS_TO_SHOW;
  const topThreeUsers = React.useMemo(() => usersData.slice(0, maxToShow), [maxToShow, usersData]);
  const hasThreeOrMore = usersDataLength >= MAX_AVATARS_TO_SHOW;

  const avatars = React.useMemo(() => {
    return topThreeUsers.map((user, id) => (
      <div key={`${user.id}-${id}`}>
        <span className="hidden lt:block">
          <AvatarUI
            src={user.avatar?.url}
            size={avatarSize.TABLET}
            username={user.username}
            walletAddress={user.publicAddress}
            borderRadius="100%"
            webappUriPath={webappUriPath(user.webappUri)}
            disableLink
            hasBorder
          />
        </span>
        <span className="block lt:hidden">
          <AvatarUI
            src={user.avatar?.url}
            size={avatarSize.MOBILE}
            username={user.username}
            walletAddress={user.publicAddress}
            borderRadius="100%"
            webappUriPath={webappUriPath(user.webappUri)}
            disableLink
            hasBorder
          />
        </span>
      </div>
    ));
  }, [avatarSize.MOBILE, avatarSize.TABLET, topThreeUsers]);

  return (
    <div
      className={twMerge(
        'relative flex flex-shrink-0 flex-row-reverse [&>*:nth-child(1)]:-ml-1.5',
        hasThreeOrMore && ' [&>*:nth-child(2)]:-ml-1.5',
      )}
    >
      {avatars}
    </div>
  );
};
